import jwtDecode from 'jwt-decode'

import { API_ROOT } from '../config'
import { CALL_API } from '../middleware/api'

export const AUTH_LOGIN_REQUEST = 'AUTH_LOGIN_REQUEST'
export const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS'
export const AUTH_LOGIN_FAILURE = 'AUTH_LOGIN_FAILURE'
export const AUTH_MFA_REQUIRED = 'AUTH_MFA_REQUIRED'

function authLoginRequest() {
  return {
    type: AUTH_LOGIN_REQUEST
  }
}

export function authLoginSuccess(token) {
  const jwt = jwtDecode(token)

  return {
    type: AUTH_LOGIN_SUCCESS,
    data: {
      id: jwt.id,
      fullname: jwt.fullname,
      role: jwt.role,
      type: jwt.type,
      category: jwt.category
    }
  }
}

function authLoginFailure(error) {
  return {
    type: AUTH_LOGIN_FAILURE,
    error
  }
}

export function authLogin(details, admin) {
  return dispatch => {
    dispatch(authLoginRequest())

    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify(details)
    }

    const route = 'login'

    return fetch(API_ROOT + route, config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json)
          }

          // Email-code MFA: the server withheld the session token and needs a
          // code. Don't store anything; hand the mfaToken back to the view.
          if (json.mfaRequired) {
            dispatch({ type: AUTH_MFA_REQUIRED })
            return { mfaRequired: true, mfaToken: json.mfaToken }
          }

          localStorage.setItem('token', json.token)

          return dispatch(authLoginSuccess(json.token))
        })
      ).catch(error => dispatch(authLoginFailure(error)))
  }
}

// Step 2 of email-code MFA: submit the emailed code with the mfaToken from
// step 1. On success it behaves exactly like a normal login (stores the token).
export function authLoginMfa(mfaToken, code) {
  return dispatch => {
    dispatch(authLoginRequest())

    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ mfaToken, code })
    }

    return fetch(API_ROOT + 'login/mfa', config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json)
          }

          localStorage.setItem('token', json.token)

          return dispatch(authLoginSuccess(json.token))
        })
      ).catch(error => dispatch(authLoginFailure(error)))
  }
}

// Magic-link completion: redeem the token carried by the email link (opened in
// this same browser). On success it behaves like a normal login (stores token).
export function authLoginMfaLink(mfaToken) {
  return dispatch => {
    dispatch(authLoginRequest())

    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ mfaToken })
    }

    return fetch(API_ROOT + 'login/mfa/link', config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json)
          }

          localStorage.setItem('token', json.token)

          return dispatch(authLoginSuccess(json.token))
        })
      ).catch(error => dispatch(authLoginFailure(error)))
  }
}

// Ask the server to email a fresh code. Returns a new mfaToken (its clock is
// reset) or an { error } object; does not touch global auth state.
export function authLoginMfaResend(mfaToken) {
  return () => {
    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ mfaToken })
    }

    return fetch(API_ROOT + 'login/mfa/resend', config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return { error: json }
          }
          return { mfaRequired: true, mfaToken: json.mfaToken }
        })
      ).catch(error => ({ error }))
  }
}

export function authLoginGoogle(credential) {
  return dispatch => {
    dispatch(authLoginRequest())

    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ credential })
    }

    return fetch(API_ROOT + 'login/google', config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json)
          }

          localStorage.setItem('token', json.token)

          return dispatch(authLoginSuccess(json.token))
        })
      ).catch(error => dispatch(authLoginFailure(error)))
  }
}

export function authLoginMicrosoft(credential) {
  return dispatch => {
    dispatch(authLoginRequest())

    const config = {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ credential })
    }

    return fetch(API_ROOT + 'login/microsoft', config)
      .then(response =>
        response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json)
          }

          localStorage.setItem('token', json.token)

          return dispatch(authLoginSuccess(json.token))
        })
      ).catch(error => dispatch(authLoginFailure(error)))
  }
}

export const AUTH_LOGOUT = 'AUTH_LOGOUT'

export function authLogout() {
  return dispatch => {
    localStorage.removeItem('token')
    dispatch({ type: AUTH_LOGOUT })
  }
}

export const AUTH_PASSWORD_RECOVERY_REQUEST = 'AUTH_PASSWORD_RECOVERY_REQUEST'
export const AUTH_PASSWORD_RECOVERY_SUCCESS = 'AUTH_PASSWORD_RECOVERY_SUCCESS'
export const AUTH_PASSWORD_RECOVERY_FAILURE = 'AUTH_PASSWORD_RECOVERY_FAILURE'

export function passwordRecoveryEmail(props) {
  return {
    [CALL_API]: {
      method: 'POST',
      endpoint: `login/passwordrecovery`,
      authenticated: false,
      body: JSON.stringify(props),
      types: [AUTH_PASSWORD_RECOVERY_REQUEST, AUTH_PASSWORD_RECOVERY_SUCCESS,
        AUTH_PASSWORD_RECOVERY_FAILURE]
    }
  }
}

export function passwordRecoveryClientEmail(props) {
  return {
    [CALL_API]: {
      method: 'POST',
      endpoint: `login/passwordrecoveryclient`,
      authenticated: false,
      body: JSON.stringify(props),
      types: [AUTH_PASSWORD_RECOVERY_REQUEST, AUTH_PASSWORD_RECOVERY_SUCCESS,
        AUTH_PASSWORD_RECOVERY_FAILURE]
    }
  }
}